import {
  Action,
  ThunkAction,
  combineReducers,
  configureStore,
} from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import ACLReducer from "reduxState/ACL/ACLSlice";
import RTLReducer from "reduxState/RTL/RTLSlice";
import achievementsReducer from "reduxState/achievements/achievements";
import adminDataReducer from "reduxState/adminData/adminDataSlice";
import campaignReducer from "reduxState/campaigns/campaigns";
import currentStoreDataReducer from "reduxState/currentStoreData/currentStoreDataSlice";
import customEventSchemasReducer from "reduxState/customEventSchemas/customEventSchemas";
import customLanguageDataReducer from "reduxState/customLanguageData/customLanguageDataSlice";
import defaultWalletDataReducer from "reduxState/defaultWalletData/defaultWalletDataSlice";
import globalManagementReducer from "reduxState/globalManagement/globalManagement";
import informationsReducer from "reduxState/informations/informationsSlice";
import loginReducer from "reduxState/login/loginSlice";
import logoutReasonReducer from "reduxState/logoutReason/logoutReasonSlice";
import crossBrowserListener from "reduxState/reduxPersistListener";
import rewardsReducer from "reduxState/rewards/rewards";
import globalSearchReducer from "reduxState/search/search";
import settingsReducer from "reduxState/settings/settings";
import sidebarReducer from "reduxState/sidebar/sidebarSlice";
import tablesReducer from "reduxState/tables/tablesSlice";
import tabsReducer from "reduxState/tabs/tabs";
import translationsReducer from "reduxState/translations/translationsSlice";
import walletsReducer from "reduxState/wallets/wallets";

export type PersistConfigType = {
  key: string;
  version: number;
  storage: typeof storage;
  whitelist?: Array<string>;
};

const tablesPersistConfig: PersistConfigType = {
  key: "tables",
  version: 1,
  storage,
};

const logoutReasonPersistConfig: PersistConfigType = {
  key: "logoutReason",
  version: 1,
  storage,
};

const currentStoreDataPersistConfig: PersistConfigType = {
  key: "store",
  version: 1,
  storage,
};

const campaignPersistConfig: PersistConfigType = {
  key: "campaigns",
  version: 1,
  storage,
  whitelist: ["popularConditions"],
};

const customLanguageDataPersistConfig: PersistConfigType = {
  key: "customLanguageData",
  version: 1,
  storage,
};

const globalSearchPersistConfig: PersistConfigType = {
  key: "search",
  version: 1,
  storage,
};

const tabsPersistConfig: PersistConfigType = {
  key: "tabs",
  version: 1,
  storage,
};

const globalManagementPersistConfig: PersistConfigType = {
  key: "globalManagement",
  version: 1,
  storage,
};

const rootReducer = combineReducers({
  achievements: achievementsReducer,
  ACL: ACLReducer,
  adminData: adminDataReducer,
  campaigns: persistReducer(campaignPersistConfig, campaignReducer),
  customEventSchemas: customEventSchemasReducer,
  customLanguageData: persistReducer(
    customLanguageDataPersistConfig,
    customLanguageDataReducer,
  ),
  defaultWalletData: defaultWalletDataReducer,
  globalManagement: persistReducer(
    globalManagementPersistConfig,
    globalManagementReducer,
  ),
  isRTL: RTLReducer,
  login: loginReducer,
  logoutReason: persistReducer(logoutReasonPersistConfig, logoutReasonReducer),
  rewards: rewardsReducer,
  search: persistReducer(globalSearchPersistConfig, globalSearchReducer),
  settings: settingsReducer,
  sidebar: sidebarReducer,
  store: persistReducer(currentStoreDataPersistConfig, currentStoreDataReducer),
  tables: persistReducer(tablesPersistConfig, tablesReducer),
  tabs: persistReducer(tabsPersistConfig, tabsReducer),
  translations: translationsReducer,
  wallets: walletsReducer,
  informations: informationsReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
      },
    }),
});

export const persistor = persistStore(store);
window.addEventListener(
  "storage",
  crossBrowserListener(store, tablesPersistConfig),
);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
